import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { environment } from '../../environments/environment';
import { AppointmentType, AppointmentTypeRequest, OperatoriesRequest, SlotsRequest } from './appointment.model';
import { AppStateService } from '../app-state.service';
import { getApiHeaders } from '../utils/api-headers.util';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {
  private baseUrl: string = environment.apiUrl;
  orgTenantId: string | null;

  // Observable for newly created appointments
  private newAppointmentSubject = new BehaviorSubject<any>(null);
  public newAppointment$ = this.newAppointmentSubject.asObservable();

  constructor(
    private http: HttpClient,
    private appStateService: AppStateService,
    private authService: AuthService,
  ) {
    this.orgTenantId = this.appStateService.getState().orgTenantId;
    // Use tenant subdomain in all environments for proper multi-tenancy
    if (this.orgTenantId) {
      this.baseUrl = this.baseUrl.replace('https://', `https://${this.orgTenantId}.`);
    }
  }

  /**
   * Emit a newly created appointment to all subscribers
   */
  emitNewAppointment(appointment: any): void {
    this.newAppointmentSubject.next(appointment);
  }

  /**
   * Clear the new appointment notification
   */
  clearNewAppointment(): void {
    this.newAppointmentSubject.next(null);
  }

  private getHeaders() {
    const authToken = this.authService.getAccessToken();
    return getApiHeaders(authToken);
  }

  fetchAppointments(client_id: string, patient_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/appointments`, {
      headers: this.getHeaders()
    });
  }

  manageAppointment(clientId: string, patientId: string, appointmentNumber: string, action: string): Observable<any> {
    const payload = {
      appointmentNumber,
      action: action,
      clientId: clientId,
      patientId: patientId
    };
    return this.http.post<any>(`${this.baseUrl}/appointment/manage`, payload, {
      headers: this.getHeaders()
    });
  }


  fetchPatient(client_id: string, patientId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/profile/${patientId}`,
      {
        headers: this.getHeaders(),
      });
  }

  fetchClientInfo(client_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/clinic_info`, {
      headers: this.getHeaders()
    });
  }

  saveAppointment(appointment_data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/patients/appointment/manage`, appointment_data, {
      headers: this.getHeaders()
    });
  }

  createAppointment(appointment_data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/appointment/book`, appointment_data, {
      headers: this.getHeaders()
    }).pipe(
      tap(response => {
        // Emit the newly created appointment for immediate UI update
        console.log('createAppointment response:', response);
        if (response && response.appointmentNumber) {
          console.log('Emitting new appointment:', response.appointmentNumber);
          this.emitNewAppointment(response);
        } else {
          console.warn('Response missing appointmentNumber:', response);
        }
      })
    );
  }

  schedulePlannedAppointment(appointment_data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/appointment/planned/schedule`, appointment_data, {
      headers: this.getHeaders()
    });
  }



  getAvailableSlots(appointment_request: SlotsRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/appointment/slots`, appointment_request, {
      headers: this.getHeaders()
    });
  }

  fetchAppointmentInfo(appointment_id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/patients/appointment/${appointment_id}`, {
      headers: this.getHeaders()
    });
  }

  getAppointmentTypes(aptRequest: AppointmentTypeRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/appointment/types`, aptRequest, {
      headers: this.getHeaders()
    });
  }

  getAppointmentTypeByDescription(description: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/appointment/types`, {
      treatmentRequest: description
    }, {
      headers: this.getHeaders()
    });
  }

  getOperatoriesList(clientId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/appointment/operatories`, {
      headers: this.getHeaders()
    });
  }

  getMatchingOperatories(request: OperatoriesRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/appointment/operatories`, request, {
      headers: this.getHeaders()
    });
  }

  getAppointmentTypeByNumber(appointmentTypeNumber: string): Observable<AppointmentType> {
    return this.http.get<AppointmentType>(`${this.baseUrl}/appointment/type/${appointmentTypeNumber}`, {
      headers: this.getHeaders()
    });
  }

}

